import { Button, Label, toast, useToggleState } from '@medusajs/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ErrorMessage from '@modules/common/components/error-message';
import Input from '@modules/common/components/input';
import { useLogin } from '@lib/query/mutations/auth';

import { ForgetPasswordModal } from './forget-password-modal';

const Login = () => {
  const navigate = useNavigate();
  const { countryCode } = useParams<{ countryCode: string }>();
  const [searchParams] = useSearchParams();
  const resetPasswordCheck = searchParams.get('reset');
  const resetRef = useRef<boolean | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { state: modalState, open, close: closeModal } = useToggleState(false);

  // Design Decision: No onError callback needed here.
  // Security monitoring (rate limiting, brute force detection) is handled
  // by the backend. Frontend error display via login.error is sufficient.
  // @see PR #63 review comment
  const login = useLogin({
    onSuccess: (customer) => {
      // Send GTM event (using dataLayer directly since we're not using next/third-parties)
      if (typeof window !== 'undefined' && (window as Window & { dataLayer?: unknown[] }).dataLayer) {
        (window as Window & { dataLayer?: unknown[] }).dataLayer?.push({
          event: 'login',
          user_id: customer.id,
        });
      }
      // Navigate to account page after successful login
      navigate(`/${countryCode ?? 'tw'}/account`);
    },
  });

  useEffect(() => {
    if (resetPasswordCheck && !resetRef.current) {
      toast.success('您的密碼已成功更新', { duration: 4000 });
      resetRef.current = true;
      setTimeout(() => {
        navigate(`/${countryCode ?? 'tw'}/account`, { replace: true });
      }, 4000);
    }
  }, [resetPasswordCheck, navigate, countryCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email: username, password });
  };

  // Extract error message from mutation error
  const errorMessage = login.error
    ? (login.error as Error & { response?: { data?: { message?: string } } })
        .response?.data?.message || '登入失敗，請檢查您的帳號密碼'
    : null;

  return (
    <div className="flex flex-col items-center" data-testid="login-page">
      <form className="w-full" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-y-2">
          <Label className="text-black-10 text-base">帳號</Label>
          <Input
            name="email"
            autoComplete="email"
            placeholder="請輸入您的電子信箱、手機號碼或統一編號"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            data-testid="email-input"
          />
          <Label className="text-black-10 text-base">密碼</Label>
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="請輸入您的密碼"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="password-input"
          />
        </div>
        <div className="flex">
          <div
            className="hover:text-black-10 ml-auto cursor-pointer"
            onClick={open}
            data-testid="login-forget-password-trigger"
          >
            忘記密碼?
          </div>
          <ForgetPasswordModal
            modalState={modalState}
            closeModal={closeModal}
            dataTestId="forget-password-modal"
            title="忘記密碼？"
            confirmText="確認送出"
          />
        </div>
        <ErrorMessage error={errorMessage} data-testid="login-error-message" />
        <div className="flex justify-center">
          <Button
            type="submit"
            data-testid="sign-in-button"
            className="bg-primary-50 text-white-10 disabled:bg-white-40 disabled:text-white-10 mt-12 rounded-sm px-6 py-3 text-base font-bold shadow-none disabled:shadow-none"
            disabled={!username || !password || login.isPending}
            isLoading={login.isPending}
          >
            會員登入
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Login;
